/**
 * Sentry Integration for CultStore - Simplified
 * Tracks checkout errors with user context
 */

window.CultStoreSentry = (function () {
  let initialized = false;
  let currentUser = null;

  /**
   * Initialize Sentry with user context
   */
  function init() {
    if (initialized || !window.Sentry || !window.Sentry.setUser) {
      return;
    }

    currentUser = getUserData();

    if (currentUser && currentUser.userId) {
      window.Sentry.setUser({
        id: currentUser.userId,
        cfUserId: currentUser.cfUserId,
        platformUserId: currentUser.platformUserId,
        email: currentUser.email,
        username: currentUser.userName,
        phone: currentUser.phone,
        isInternalUser: currentUser.isInternalUser,
      });
    }

    if (window.Sentry.setTag) {
      window.Sentry.setTag('environment', 'production');
      window.Sentry.setTag('service', 'cultstore-theme');
    }
    
    initialized = true;
    console.log('[Sentry] Initialized with user context');
  }

  /**
   * Get user data from localStorage
   */
  function getUserData() {
    try {
      const customer = JSON.parse(localStorage.getItem('customer'));
      if (!customer) return null;

      return {
        userId: customer?.metafields?.cult?.cfuserid || customer?.id || null,
        cfUserId: customer?.metafields?.cult?.cfuserid || null,
        platformUserId: customer?.id || null,
        email: customer?.metafields?.cult?.email || customer?.email || '',
        phone: customer?.phone || '',
        userName: `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim(),
        isInternalUser: customer?.metafields?.cult?.is_internal_user || false,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Update user context
   */
  function updateUserContext() {
    if (!window.Sentry || !window.Sentry.setUser) return;

    currentUser = getUserData();
    if (currentUser && currentUser.userId) {
      window.Sentry.setUser({
        id: currentUser.userId,
        cfUserId: currentUser.cfUserId,
        platformUserId: currentUser.platformUserId,
        email: currentUser.email,
        username: currentUser.userName,
        phone: currentUser.phone,
        isInternalUser: currentUser.isInternalUser,
      });
    }
  }

  /**
   * Capture checkout error with context
   */
  function captureCheckoutError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }

    if (!currentUser) {
      updateUserContext();
    }

    // Add cart data
    let cartData = null;
    try {
      const cart = JSON.parse(localStorage.getItem('cart'));
      if (cart) {
        cartData = {
          itemCount: cart.item_count,
          totalPrice: cart.total_price,
          items: cart.items?.map(item => ({
            id: item.id,
            title: item.title,
            quantity: item.quantity,
            price: item.price,
          })) || [],
        };
      }
    } catch (e) {
      // Ignore
    }

    const enrichedContext = {
      tags: {
        error_type: 'CHECKOUT_TRACKER',
        checkout_stage: context.stage || 'unknown',
        user_id: currentUser?.userId || 'guest',
      },
      extra: {
        ...context,
        cart: cartData,
        pageUrl: window.location.href,
        timestamp: new Date().toISOString(),
      },
    };

    window.Sentry.captureException(error, enrichedContext);
  }

  /**
   * Add breadcrumb
   */
  function addBreadcrumb(message, category = 'user-action', data = {}) {
    if (!window.Sentry || !window.Sentry.addBreadcrumb) return;

    window.Sentry.addBreadcrumb({
      message: message,
      category: category,
      level: 'info',
      data: data,
    });
  }

  // Initialize when Sentry loads (with retry logic)
  if (window.Sentry && window.Sentry.setUser) {
    init();
  } else {
    // Retry with delays to ensure Sentry SDK is fully loaded
    setTimeout(init, 500);
    setTimeout(init, 1500);
    setTimeout(init, 3000);
  }

  // Update user context on storage change
  window.addEventListener('storage', function (e) {
    if (e.key === 'customer') {
      updateUserContext();
    }
  });

  return {
    init,
    updateUserContext,
    captureCheckoutError,
    addBreadcrumb,
  };
})();
